import setQuery from '@/lib/setQuery';
import setPref from '@/redux/actions/uiActions/setPref';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import { useCallback } from 'react';
import { getDateRangeFromAction } from '@/lib/dateRangeActions';

const formatDate = (date, timeFilter = null) => {
  if (!date) {
    return null;
  }

  const formattedDate = new Date(date);

  // Apply time filter if provided
  if (timeFilter?.startTime) {
    const [hours, minutes] = timeFilter.startTime.split(':').map(Number);
    formattedDate.setHours(hours, minutes, 0, 0);
  } else {
    // Preserve non-midnight times for ranges like "Last 24 hours"
    const isAtMidnight = formattedDate.getHours() === 0 && formattedDate.getMinutes() === 0 && formattedDate.getSeconds() === 0 && formattedDate.getMilliseconds() === 0;

    if (isAtMidnight) {
      formattedDate.setHours(0, 0, 0, 0);
    }
  }

  return formattedDate.toISOString();
};

const formatDateTo = (date, timeFilter = null) => {
  if (!date) {
    return null;
  }

  const formattedDate = new Date(date);

  // Apply time filter if provided
  if (timeFilter?.endTime) {
    const [hours, minutes] = timeFilter.endTime.split(':').map(Number);
    formattedDate.setHours(hours, minutes, 59, 999);
    return formattedDate.toISOString();
  }

  // Preserve non-midnight times for ranges like "Last 24 hours"
  const isAtMidnight = formattedDate.getHours() === 0 && formattedDate.getMinutes() === 0 && formattedDate.getSeconds() === 0 && formattedDate.getMilliseconds() === 0;

  if (!isAtMidnight) {
    return formattedDate.toISOString();
  }

  // If it's at midnight, set to end of day (for regular date selections)
  formattedDate.setHours(23, 59, 59, 999);

  return formattedDate.toISOString();
};

const useWorkflowLogsFilters = () => {
  const workflowIdParam = useArgSelector(selectors.param, 'workflowId');
  const workflowIdQuery = useArgSelector(selectors.query, 'workflowId');
  const workflowId = workflowIdParam || workflowIdQuery;

  const dateRangeFilter = useArgSelector(selectors.pref, `workflowLogsDateRangeFilter-${workflowId}`) || null;
  const snapshotFilter = useArgSelector(selectors.pref, `workflowLogsSnapshotFilter-${workflowId}`) || [];
  const environmentFilter = useArgSelector(selectors.pref, `workflowLogsEnvironmentFilter-${workflowId}`) || [];
  const statusFilter = useArgSelector(selectors.pref, `workflowLogsStatusFilter-${workflowId}`) || [];
  const activityFilter = useArgSelector(selectors.pref, `workflowLogsActivityFilter-${workflowId}`) || [];
  const endUserFilter = useArgSelector(selectors.pref, `workflowLogsEndUserFilter-${workflowId}`) || [];
  const installationFilter = useArgSelector(selectors.pref, `workflowLogsInstallationFilter-${workflowId}`) || [];
  const searchValue = useArgSelector(selectors.query, 'search') || '';

  const getPreparedFilters = useCallback(
    (filter = null, value = null) => {
      let actualDateRange = null;
      let timeFilter = null;

      if (filter === 'dateRange') {
        if (value?.action) {
          actualDateRange = getDateRangeFromAction(value.action);
        } else {
          actualDateRange = value;
        }
        // Extract time filter from the value
        timeFilter = value?._timeFilter || null;
      } else {
        if (dateRangeFilter?.action) {
          actualDateRange = getDateRangeFromAction(dateRangeFilter.action);
        } else {
          actualDateRange = dateRangeFilter;
        }
        // Extract time filter from stored filter
        timeFilter = dateRangeFilter?._timeFilter || null;
      }

      const filters = {
        dateRangeFilterFrom: formatDate(actualDateRange?.from, timeFilter) ?? undefined,
        dateRangeFilterTo: formatDateTo(actualDateRange?.to, timeFilter) ?? undefined,
        snapshotFilter: filter === 'snapshot' ? value?.join(',') : snapshotFilter?.length > 0 ? snapshotFilter.join(',') : undefined,
        environmentFilter: filter === 'environment' ? value?.join(',') : environmentFilter?.length > 0 ? environmentFilter.join(',') : undefined,
        statusFilter: filter === 'status' ? value?.join(',') : statusFilter?.length > 0 ? statusFilter.join(',') : undefined,
        endUserFilter: filter === 'endUser' ? value?.join(',') : endUserFilter?.length > 0 ? endUserFilter.join(',') : undefined,
        installationFilter: filter === 'installation' ? value?.join(',') : installationFilter?.length > 0 ? installationFilter.join(',') : undefined,
        activityFilter: filter === 'activity' ? value?.join(',') : activityFilter?.length > 0 ? activityFilter.join(',') : undefined,
        search: searchValue || undefined
      };

      return filters;
    },
    [dateRangeFilter, snapshotFilter, environmentFilter, statusFilter, activityFilter, endUserFilter, installationFilter, searchValue]
  );

  /**
   * Handles workflow log filter changes
   * The state handler will automatically fetch updated data when filters change
   * @param {string} filter - The filter type to change (only 'dateRange', 'snapshot', 'environment', 'status', 'endUser', 'installation' or 'activity')
   * @param {*} value - The new value for the filter
   */
  const handleFilterChange = useCallback(
    (filter, value) => {
      if (!workflowId) {
        return;
      }

      const filterKey = filter?.charAt(0)?.toUpperCase() + filter?.slice(1);
      setPref(`workflowLogs${filterKey}Filter-${workflowId}`, value);
      setQuery({ logId: 'summary' });
    },
    [workflowId]
  );

  return { handleFilterChange, getPreparedFilters };
};

export default useWorkflowLogsFilters;
